import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { trackEvent, trackRfqSubmitted, WIZARD_STEPS } from "@/lib/mag-analytics";
import { serverMessage } from "@/lib/serverMessage";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import type { IncomeCertificateSubmitPayload } from "@/components/features/notarial-requests/income-certificate-wizard";
import type { IncomeCertificateWizardHandle } from "@/components/features/notarial-requests/income-certificate-wizard";
import type { LegalDocumentType } from "@/components/features/notarial-requests/legal-entity-request-fields";
import type { AddressSelection } from "@/components/shared/AddressPicker";
import {
  ALL_CERTIFICATE_TYPES,
  typesForCategory,
  type RequestCategory,
} from "@/components/features/notarial-requests/new-notarial-request-types";
import {
  isValidateRutServiceUnavailableError,
  validateRut,
  type ValidateRutResponse,
} from "@/services/authService";
import {
  createMyAccountantRequest,
  uploadMyAccountantRequestDocument,
  type ConsumerCertificateRequestSource,
} from "@/services/userAccountantRequestsService";
import {
  createMyNotarialRequest,
  uploadMyNotarialRequestDocument,
  type UserNotarialRequestDocumentType,
} from "@/services/userNotarialRequestsService";
import {
  createQuoteRequest,
  contactDetailKindsFromError,
  type ContactDetailKind,
  type QuoteRequestAttachmentUpload,
} from "@/services/quoteRequestsService";
import { DEFAULT_CERTIFICATE_FULFILLMENT_MODES_ALLOWED } from "@/constants/certificateFulfillmentModes";
import {
  applyCatalogDigitalDeliveryDeduction,
  catalogCustomerAmountMinor,
  catalogItemForType,
  countIncomeCertificateBillableFiles,
  estimatePerFileCertificateTotalMinor,
  formatCatalogAmountMinor,
  getCertificatePricingCatalog,
} from "@/services/certificatePricingCatalogService";
import { createCertificateCheckout } from "@/services/certificatePaymentsService";
import {
  buildIncomeCertificatePaymentResume,
  buildLegalEntityPaymentResume,
  putCertificatePaymentResume,
} from "@/utils/certificatePaymentResume";
import {
  isCheckoutAutoPaidBypass,
  redirectToCertificateCheckout,
  toastIfCheckoutAutoPaidBypass,
} from "@/utils/certificatePaymentFlow";
import type { CertificateCheckoutPaymentMethod } from "@/services/certificatePaymentsService";
import {
  runCertificatePaymentResumeFlow,
  type ResumeProgress,
} from "@/utils/certificatePaymentResumeRunner";
import {
  categoryForCatalogProductType,
  flowKindForProductType,
} from "@/utils/certificateRequestFlow";
import { getCertificateRequestIntents } from "@/services/certificateRequestIntentsService";
import {
  getEnterpriseCatalog,
  createEnterpriseRequest,
  listMyEnterpriseRequests,
  completeMyEnterpriseRequestStep,
  getEnterpriseRequestNodeDefaults,
  type EnterpriseCatalogEntry,
  type EnterpriseRequest,
} from "@/services/enterpriseCatalogService";
import { useAuth } from "@/contexts/AuthContext";
import { useIncomeCertificateWizardFormState } from "@/hooks/useIncomeCertificateWizardFormState";
import {
  clearWizardSessionUuid,
  clearWizardState,
  getOrCreateWizardSessionUuid,
  hydrateWizardState,
  snapshotWizardState,
} from "@/utils/wizardStatePersistence";
import {
  EMPTY_MANUAL_COMPANY_FIELDS,
  manualCompanyFieldsToValidateRutResponse,
  registrationSnapshotToValidateRutResponse,
  type ManualCompanyFieldsState,
} from "@/utils/manualCompanySnapshot";
import {
  getQuoteIntake,
  isIntakeUnavailable,
  QUOTE_INTAKE_MAX_TURNS,
  sendQuoteIntakeMessage,
  startQuoteIntake,
  type QuoteIntakeAnticipatedDocument,
  type QuoteIntakeDraft,
  type QuoteIntakeMessage,
} from "@/services/quoteIntakeService";

/**
 * URL param carrying the intake conversation, so a refresh resumes instead of
 * silently discarding the turns the consumer already answered.
 */
const INTAKE_CONVERSATION_PARAM = "c";

const CERTIFICATE_CATALOG_FLOW_KEY = "certificate_catalog" as const;
const ENTERPRISE_CATALOG_FLOW_KEY = "enterprise_catalog" as const;

/**
 * Module-scoped map of in-flight payment-resume runners keyed by checkoutId. Re-runs
 * of the resume effect (StrictMode double-mount, accidental re-renders) attach to the
 * existing promise instead of starting a second runner. The first runner owns the
 * IndexedDB take-and-create work; observers just await its completion.
 */
const inFlightResumeRunners = new Map<string, Promise<void>>();

export type NewCertificateRequestWizardStep =
  | "chooseIntent"
  | "chooseType"
  | "chooseEnterpriseProduct"
  | "preconditionCheck"
  | "fillEnterpriseProduct"
  | "workflowSteps"
  | "legalEntityDetails"
  | "incomeCertificate"
  | "quoteRequest";

export type LegalEntityWizardPhase = "form" | "summary";

/**
 * "chat" is the default entry point (spec add326e0): the consumer describes
 * the need conversationally and the AI triages it while they talk. "form" is
 * BOTH the editable confirmation of what the chat drafted AND the fallback
 * when the model is unavailable — intake must never be blocked by the AI
 * being down, so the static fields stay reachable either way.
 */
/**
 * "delivery" sits between form and summary and is SKIPPED entirely when the
 * trámite produces nothing to ship (spec 8c2aab49 D3). It is a phase rather
 * than a block inside the summary because the address decides the shipping
 * price, and a consumer must settle it before the screen that states a total.
 */
export type QuoteRequestWizardPhase = "chat" | "form" | "delivery" | "summary";

/** Company RUT entry: registered account company vs manual validate, or picking between them. */
export type LegalEntityCompanyRutMode = "choose" | "session" | "other";

export interface UseNewCertificateRequestFlowOptions {
  onRequestCreated: (requestId: string, source: ConsumerCertificateRequestSource) => void;
  /** Set when returning from full-page dLocal checkout (`?certificate_payment=done&checkout_id=`). */
  paymentResumeCheckoutId: string | null;
  /** Set when launched from an enterprise workflow node (`?returnEnterpriseRequest=&returnStep=`). */
  returnEnterpriseRequestId?: string | null;
  returnStepKey?: string | null;
  /**
   * True when an enterprise workflow node sent the visitor here WITHOUT naming
   * a product, so they must pick one from the catalog.
   *
   * It needs saying explicitly because the certificates intent is hidden and
   * the sole-intent auto-select would otherwise drop them into the RFQ chat —
   * which is not what a graph node spawning a child certificate request
   * means. `ChildRequestActions` omits the product on purpose for nodes that
   * leave the choice open.
   */
  enterpriseCatalogEntry?: boolean;
  /**
   * Wizard entry type — the catalog targetType from the URL `?type=` deep-link,
   * or `"generic"` when no product context exists at mount.
   *
   * No longer feeds an analytics event: wizard_product_selected was removed on
   * 2026-10-09. Still passed because callers set it and it describes how the
   * wizard was entered.
   */
  initialWizardType?: string;
}

/**
 * A list-scannable title from the consumer's own opening line, for the
 * model-unavailable fallback. Mirrors deriveTitleFromTranscript on the backend
 * (60 chars, newlines collapsed) so a request reads the same whichever path
 * produced it.
 */
function deriveQuoteTitle(firstMessage: string | undefined): string {
  const text = (firstMessage ?? "").replace(/\s+/g, " ").trim();
  if (!text) return "";
  return text.length > 60 ? `${text.slice(0, 57)}...` : text;
}

export function useNewCertificateRequestFlow({
  onRequestCreated,
  paymentResumeCheckoutId,
  returnEnterpriseRequestId,
  returnStepKey,
  enterpriseCatalogEntry = false,
  initialWizardType,
}: UseNewCertificateRequestFlowOptions) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  /** Parent often passes an inline callback; must not be a useEffect dep or resume re-runs every render → GET storm + 429. */
  const onRequestCreatedRef = useRef(onRequestCreated);
  onRequestCreatedRef.current = onRequestCreated;
  const [step, setStep] = useState<NewCertificateRequestWizardStep>("chooseIntent");
  const [selectedIntentSlug, setSelectedIntentSlug] = useState<string | null>(null);
  const [category, setCategory] = useState<RequestCategory | null>(null);
  const [requestType, setRequestType] = useState<string | null>(null);

  /**
   * Mirror current step into the URL so support and the visitor can see at a
   * glance where they are. `replace` (not push) keeps history clean — browser
   * back exits the wizard cleanly rather than walking back through every step.
   * All other params (`type`, the three resume params,
   * `returnEnterpriseRequest`, utms, …) are preserved.
   *
   * THIS EFFECT EMITS NOTHING. The funnel events it used to share a body with
   * were removed on 2026-10-09; `wizard_step_viewed` came back on 2026-10-13
   * (see below) but deliberately in an effect of its own. Mirroring the step to
   * the URL is not analytics, and the one place the 2026-10-09 removal had to
   * be careful rather than wholesale was exactly this entanglement.
   */
  useEffect(() => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set("step", WIZARD_STEPS[step].name);
        return next;
      },
      { replace: true },
    );
  }, [step, setSearchParams]);

  /**
   * `wizard_step_viewed` — the one mid-wizard event, restored 2026-10-13.
   *
   * WHY IT IS BACK, HAVING BEEN DELETED ON 2026-10-09. That removal took out
   * ten funnel events on the grounds that they were "volume without a question
   * attached to them", and it was right at the time. There is a question now:
   * ~80% of visitors are lost between `wizard_started` and
   * `rfq_first_message_sent`, and the two events alone can only say that people
   * entered and did not finish. They cannot say WHERE.
   *
   * This one event splits that 80% into the two buckets that imply different
   * fixes. An RFQ visitor passes `chooseIntent` (index 1) and then
   * `quoteRequest` (index 2), where the chat lives:
   *
   *   - no `wizard_step_viewed{step_name: "quote_request"}` → they bailed on the
   *     first screen, before ever reaching the conversation.
   *   - that event but no `rfq_first_message_sent` → they reached the
   *     conversation and never typed.
   *
   * ONLY THIS ONE CAME BACK. `wizard_step_completed` and
   * `wizard_product_selected` stay deleted: the first is derivable from a pair
   * of viewed events and the second answers nothing being asked. Re-adding the
   * whole set would recreate the volume the removal was right about.
   *
   * The ref holds the step most recently TRACKED (not the one before it), so
   * the guard is an equality check rather than an off-by-one.
   *
   * DE-DUPED ON THE STEP, which the original did too. The effect must not fire
   * twice for one step view, because the ratio between these events IS the
   * measurement — a double here reads as a visitor who looked at the step twice.
   * `requestType` is read through a ref so that a type arriving later does not
   * re-fire the event for a step already counted.
   *
   * `trackEvent` swallows its own failures, so nothing here can break the
   * wizard — see its body, and the project rule it implements.
   */
  const lastTrackedStepRef = useRef<NewCertificateRequestWizardStep | null>(null);
  const requestTypeRef = useRef<string | null>(requestType);
  requestTypeRef.current = requestType;
  useEffect(() => {
    if (lastTrackedStepRef.current === step) return;
    lastTrackedStepRef.current = step;
    trackEvent("wizard_step_viewed", {
      step_index: WIZARD_STEPS[step].index,
      step_name: WIZARD_STEPS[step].name,
      service_type: requestTypeRef.current,
    });
  }, [step]);

  /**
   * Mirror the selected product type to `?type=` so the URL shows both pieces
   * (`?type=income_certificate&step=…`) once the visitor has picked one. We
   * only WRITE — never DELETE — so the existing landing→wizard deep-link
   * convention keeps working and back-navigation to chooseIntent doesn't
   * blow away the carried-in `?type=` mid-render. `replace` keeps history
   * tidy alongside the step mirror.
   */
  useEffect(() => {
    if (requestType == null || requestType === "") return;
    setSearchParams(
      (current) => {
        if (current.get("type") === requestType) return current;
        const next = new URLSearchParams(current);
        next.set("type", requestType);
        return next;
      },
      { replace: true },
    );
  }, [requestType, setSearchParams]);
  const [rut, setRut] = useState("");
  const [validatedCompany, setValidatedCompany] = useState<ValidateRutResponse | null>(null);
  const [legalEntityManualMode, setLegalEntityManualMode] = useState(false);
  const [legalEntityManualFields, setLegalEntityManualFields] = useState<ManualCompanyFieldsState>(
    EMPTY_MANUAL_COMPANY_FIELDS,
  );
  const [isValidatingRut, setIsValidatingRut] = useState(false);
  const [estatutoFile, setEstatutoFile] = useState<File | null>(null);
  const [comunicacionBcuFile, setComunicacionBcuFile] = useState<File | null>(null);
  const [legalDocumentType, setLegalDocumentType] = useState<LegalDocumentType>("national_id");
  const [legalDocumentFrontFile, setLegalDocumentFrontFile] = useState<File | null>(null);
  const [legalDocumentBackFile, setLegalDocumentBackFile] = useState<File | null>(null);
  const [passportDocumentFile, setPassportDocumentFile] = useState<File | null>(null);
  const [legalEntityPhase, setLegalEntityPhase] = useState<LegalEntityWizardPhase>("form");
  const [quoteRequestPhase, setQuoteRequestPhase] = useState<QuoteRequestWizardPhase>("chat");

  /**
   * `rfq_phase_viewed` — the four screens INSIDE the quote_request step.
   *
   * WHY `wizard_step_viewed` CANNOT ANSWER THIS, as its own note above says:
   * the step stays `quote_request` across the whole RFQ, so everything from
   * the first chat message to the final submit is one event. A consumer who
   * reaches a finished draft and leaves is indistinguishable from one who
   * abandons at the postal address or at the summary, and those three have
   * completely different fixes.
   *
   * THE CASE THAT FORCED IT, 2026-10-21: a consumer registered, chatted to a
   * complete draft (accountant, 0.95 confidence, an easy job) and submitted
   * nothing. She never saw the login wall — she was already authenticated —
   * so the auth-gate events say nothing about her, and the funnel recorded a
   * first message and no submission. Four consumers have now reached a
   * complete draft and one has submitted.
   *
   * IT ALSO ANSWERS AN OPEN CONTROL. The spec that made the delivery step
   * unconditional accepted a real cost — "a consumer whose deliverable is a
   * PDF still gives a postal address" — and named the metric to watch:
   * "wizard completion on the delivery step (control C-5)". Nothing has ever
   * measured it. This is that measurement.
   *
   * DE-DUPED ON THE PHASE, for the reason its neighbour is: the ratio between
   * these events IS the measurement, so a double reads as a consumer who
   * visited a screen twice. Going BACK and forward again re-fires on purpose —
   * that is a real second visit, and a consumer bouncing between the address
   * form and the review is exactly the shape worth seeing.
   *
   * `trackEvent` swallows its own failures, so nothing here can break the
   * wizard.
   */
  const lastTrackedPhaseRef = useRef<QuoteRequestWizardPhase | null>(null);
  useEffect(() => {
    if (step !== "quoteRequest") {
      // Leaving the step resets the guard, so returning to the RFQ re-counts
      // its first phase instead of silently skipping it.
      lastTrackedPhaseRef.current = null;
      return;
    }
    if (lastTrackedPhaseRef.current === quoteRequestPhase) return;
    lastTrackedPhaseRef.current = quoteRequestPhase;
    trackEvent("rfq_phase_viewed", {
      // The phase name is the whole point; there is no index because the
      // four are not a fixed sequence — `delivery` can be re-entered from
      // `summary` by going back.
      step_id: quoteRequestPhase,
    });
  }, [step, quoteRequestPhase]);

  /** From the intake draft. False for the static fallback form, which asks nothing. */
  const [quoteDeliveryAddressSelection, setQuoteDeliveryAddressSelection] =
    useState<AddressSelection>(null);
  /** The id of the address actually saved for this request, once created. */
  const [quoteDeliveryAddressId, setQuoteDeliveryAddressId] = useState<string | null>(null);
  /*
    "I have no Uruguayan address yet" (spec e370e36f). Sent to the server,
    where it clears producesPhysicalDocument — without that the request passes
    the wizard and then fails assertDeliverableIfPhysical at broadcast, which
    reads as a server fault rather than the consumer's own answer.
  */
  const [quoteDeliveryOptedOut, setQuoteDeliveryOptedOut] = useState(false);
  const [quoteIntakeConversationId, setQuoteIntakeConversationId] = useState<string | null>(null);
  const [quoteIntakeMessages, setQuoteIntakeMessages] = useState<QuoteIntakeMessage[]>([]);
  const [quoteIntakeThinking, setQuoteIntakeThinking] = useState(false);
  const [quoteIntakeTurnsRemaining, setQuoteIntakeTurnsRemaining] =
    useState(QUOTE_INTAKE_MAX_TURNS);
  const [quoteIntakeOutOfScopeMessage, setQuoteIntakeOutOfScopeMessage] = useState<string | null>(null);
  /**
   * The conversation id to submit WITH the request, set only once a turn came
   * back drafted. Deliberately separate from quoteIntakeConversationId: after a
   * fallback to the static form the conversation still exists but never
   * produced a draft, and submitting its id would be rejected.
   */
  const [quoteIntakeDraftConversationId, setQuoteIntakeDraftConversationId] = useState<
    string | null
  >(null);
  const [legalEntityFulfillmentMode, setLegalEntityFulfillmentMode] = useState<string>("physical_only");
  const [incomeInnerStep, setIncomeInnerStep] = useState<1 | 2 | 3>(1);
  const [incomePrimaryEnabled, setIncomePrimaryEnabled] = useState(false);
  const incomeWizardRef = useRef<IncomeCertificateWizardHandle>(null);
  const [isCertificatePaymentBusy, setIsCertificatePaymentBusy] = useState(false);
  const [incomeFlowPhase, setIncomeFlowPhase] = useState<"idle" | "payment" | "create">("idle");

  /**
   * How the consumer chose to pay. Held here rather than threaded through the
   * four submit functions and their callers: the choice is made once, in the
   * payment step, and every checkout this hook creates must honour it.
   *
   * Defaults to "dlocal" so any surface that never renders the selector keeps
   * the existing card behaviour untouched.
   */
  const [paymentMethod, setPaymentMethod] =
    useState<CertificateCheckoutPaymentMethod>("dlocal");

  /**
   * Reset the method whenever the product changes.
   *
   * Only two flows render the selector (income certificate and legal entity);
   * the simple notary/accountant submits have no summary screen and so no
   * choice. Without this reset, a consumer who picked bank transfer on one
   * product and then switched to one of those would silently get a
   * bank-transfer checkout they never asked for — and a screen asking them to
   * make a transfer, with no way back to the card they expected.
   *
   * Resetting on the PRODUCT rather than on unmount keeps it correct within a
   * single wizard session, which is where the leak lives.
   */
  useEffect(() => {
    setPaymentMethod("dlocal");
  }, [requestType]);
  const [isPaymentResumeRunning, setIsPaymentResumeRunning] = useState(false);
  const [paymentResumeProgress, setPaymentResumeProgress] = useState<ResumeProgress | null>(null);
  const [incomeReviewPhase, setIncomeReviewPhase] = useState<"wizard" | "summary">("wizard");
  const [incomeReviewPayload, setIncomeReviewPayload] = useState<IncomeCertificateSubmitPayload | null>(
    null,
  );
  const [legalEntityCompanyRutMode, setLegalEntityCompanyRutMode] =
    useState<LegalEntityCompanyRutMode>("other");
  const [legalEntityInvoiceIncludeRut, setLegalEntityInvoiceIncludeRut] = useState(false);
  const [legalEntityInvoiceRutDigits, setLegalEntityInvoiceRutDigits] = useState("");
  const [selectedEnterpriseProduct, setSelectedEnterpriseProduct] = useState<EnterpriseCatalogEntry | null>(null);
  const [enterpriseFormSubmitting, setEnterpriseFormSubmitting] = useState(false);
  const [activeEnterpriseRequest, setActiveEnterpriseRequest] = useState<EnterpriseRequest | null>(null);
  /** When set, the wizard renders this node instead of the workflow's currentNodeId (for back navigation). */
  const [workflowViewNodeId, setWorkflowViewNodeId] = useState<string | null>(null);
  /** Accumulated step data collected client-side before request creation. */
  const [pendingStepData, setPendingStepData] = useState<Record<string, Record<string, unknown>>>({});
  const [incomeInvoiceIncludeRut, setIncomeInvoiceIncludeRut] = useState(false);
  const [incomeInvoiceRutDigits, setIncomeInvoiceRutDigits] = useState("");

  /* Quote Request form states */
  const [quoteRequestTitle, setQuoteRequestTitle] = useState("");
  const [quoteRequestDescription, setQuoteRequestDescription] = useState("");
  const [quoteRequestAttachments, setQuoteRequestAttachments] = useState<
    QuoteRequestAttachmentUpload[]
  >([]);
  /**
   * The documents the conversation suggested attaching, if any. Held apart
   * from the draft title/description because the static fallback form has no
   * conversation behind it and therefore no suggestions — it renders generic
   * slots, as it always has.
   */
  const [quoteAnticipatedDocuments, setQuoteAnticipatedDocuments] = useState<
    QuoteIntakeAnticipatedDocument[]
  >([]);
  /**
   * What the consumer typed into the text-kind anticipated items, keyed by the
   * item's label (spec 13810fd2) — an RUT, a número de BPS.
   *
   * Kept here rather than in the review component so an answer survives
   * stepping back to the conversation and forward again, exactly as the
   * attachments do.
   */
  const [quoteIntakeAnswers, setQuoteIntakeAnswers] = useState<Record<string, string>>({});
  /**
   * Which professions the conversation concluded this needs — one, or two when
   * the job spans both.
   *
   * Kept so the review step can say who is about to be asked to quote. Two
   * disciplines means two quotes and two separate charges, and the consumer
   * should learn that before submitting rather than when the second invoice
   * arrives.
   */
  const [quoteDraftDisciplines, setQuoteDraftDisciplines] = useState<
    Array<"notary" | "accountant">
  >([]);
  /**
   * The typed answers as they will actually be SENT: only items the intake
   * asked for, only the ones with an answer, trimmed.
   *
   * Derived once and shared by the payload and the final summary screen, so
   * the list the consumer is shown before pressing the button is the same list
   * the button sends. An answer left behind by an item the conversation later
   * dropped is excluded — it would reach the professional as a question nobody
   * asked.
   */
  const quoteIntakeDetails = useMemo(
    () =>
      quoteAnticipatedDocuments
        .map((doc) => ({ label: doc.label, value: (quoteIntakeAnswers[doc.label] ?? "").trim() }))
        .filter((detail) => detail.value !== ""),
    [quoteAnticipatedDocuments, quoteIntakeAnswers],
  );

  const buildQuoteRequestPayload = useCallback(
    () => ({
      title: quoteRequestTitle,
      description: quoteRequestDescription,
      attachments: quoteRequestAttachments,
      intakeDetails: quoteIntakeDetails,
      intakeConversationId: quoteIntakeDraftConversationId,
      deliveryAddressId: quoteDeliveryAddressId ?? undefined,
      noUruguayanDeliveryAddress: quoteDeliveryOptedOut ? true : undefined,
    }),
    [
      quoteRequestTitle,
      quoteRequestDescription,
      quoteRequestAttachments,
      quoteIntakeDetails,
      quoteIntakeDraftConversationId,
      quoteDeliveryAddressId,
      quoteDeliveryOptedOut,
    ],
  );


  /**
   * Mirror wizard sub-state to `?phase=` so the URL captures finer-grained
   * progress than the top-level `?step=`. Value namespace is disambiguated
   * by the current `?step=`:
   *   - step=income_certificate → phase=1 / 2 / 3 / summary
   *   - step=legal_entity_details → phase=form / summary
   *   - step=workflow_steps → phase=<nodeId> when the visitor manually
   *     navigates to a specific node; omitted while auto-driven by the
   *     workflow runner (the rendered node is then implicit from the
   *     graph's currentNodeId server state, which we'd need to traverse
   *     to compute — out of scope for the URL mirror).
   *
   * Other steps have no meaningful sub-state; `phase` is deleted when not
   * applicable so the URL stays clean.
   */
  useEffect(() => {
    let phase: string | null = null;
    if (step === "incomeCertificate") {
      phase = incomeReviewPhase === "summary" ? "summary" : String(incomeInnerStep);
    } else if (step === "legalEntityDetails") {
      phase = legalEntityPhase;
    } else if (step === "workflowSteps" && workflowViewNodeId != null) {
      phase = workflowViewNodeId;
    }
    setSearchParams(
      (current) => {
        const existing = current.get("phase");
        if (phase != null && phase !== "") {
          if (existing === phase) return current;
          const next = new URLSearchParams(current);
          next.set("phase", phase);
          return next;
        }
        if (existing == null) return current;
        const next = new URLSearchParams(current);
        next.delete("phase");
        return next;
      },
      { replace: true },
    );
  }, [
    step,
    incomeInnerStep,
    incomeReviewPhase,
    legalEntityPhase,
    workflowViewNodeId,
    setSearchParams,
  ]);

  const catalogQuery = useQuery({
    queryKey: ["me", "certificate-pricing", "catalog"] as const,
    queryFn: getCertificatePricingCatalog,
    enabled: true,
    staleTime: 60_000,
  });

  const incomeAllowedFulfillmentModes = useMemo(() => {
    const item = catalogItemForType(catalogQuery.data ?? [], "income_certificate");
    return item?.fulfillmentModesAllowed != null && item.fulfillmentModesAllowed.length > 0
      ? item.fulfillmentModesAllowed
      : [...DEFAULT_CERTIFICATE_FULFILLMENT_MODES_ALLOWED];
  }, [catalogQuery.data]);

  const nodeDefaultsQuery = useQuery({
    queryKey: ['enterprise-request-node-defaults', returnEnterpriseRequestId, returnStepKey],
    queryFn: () => getEnterpriseRequestNodeDefaults(returnEnterpriseRequestId!, returnStepKey!),
    enabled: Boolean(returnEnterpriseRequestId) && Boolean(returnStepKey),
  });

  const incomeForm = useIncomeCertificateWizardFormState(
    incomeAllowedFulfillmentModes,
    nodeDefaultsQuery.data ?? null,
  );

  const certificateRequestIntentsQuery = useQuery({
    queryKey: ["me", "certificate-request-intents"] as const,
    queryFn: getCertificateRequestIntents,
    staleTime: 60_000,
  });

  const enterpriseCatalogCategory = useMemo((): string | null => {
    if (step !== "chooseEnterpriseProduct" || !selectedIntentSlug) return null;
    const row = certificateRequestIntentsQuery.data?.find((r) => r.slug === selectedIntentSlug);
    if (row?.flowKey !== ENTERPRISE_CATALOG_FLOW_KEY) return null;
    return selectedIntentSlug;
  }, [step, selectedIntentSlug, certificateRequestIntentsQuery.data]);

  const enterpriseCatalogQuery = useQuery({
    queryKey: ["me", "certificate-pricing", "enterprise-catalog", enterpriseCatalogCategory] as const,
    queryFn: () => getEnterpriseCatalog(enterpriseCatalogCategory!),
    enabled: enterpriseCatalogCategory != null,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (certificateRequestIntentsQuery.isError) {
      toast.error(t("requests.user.wizard.intentsLoadError"));
    }
  }, [certificateRequestIntentsQuery.isError, t]);

  useEffect(() => {
    const item = catalogItemForType(catalogQuery.data ?? [], "notarial_legal_entity");
    const allowed =
      item?.fulfillmentModesAllowed != null && item.fulfillmentModesAllowed.length > 0
        ? item.fulfillmentModesAllowed
        : [...DEFAULT_CERTIFICATE_FULFILLMENT_MODES_ALLOWED];
    setLegalEntityFulfillmentMode((prev) =>
      allowed.includes(prev) ? prev : allowed[0] ?? "physical_only",
    );
  }, [catalogQuery.data]);

  const allowedTypes = useMemo((): string[] => {
    if (step !== "chooseType") return [];
    const catalog = catalogQuery.data;
    if (!catalog?.length) return [...ALL_CERTIFICATE_TYPES];
    return catalog.map((c) => c.targetType);
  }, [step, catalogQuery.data]);

  const legalEntityChargeTotalLabel = useMemo((): string | null => {
    if (requestType !== "notarial_legal_entity") return null;
    const item = catalogItemForType(catalogQuery.data ?? [], "notarial_legal_entity");
    const charge = item != null ? catalogCustomerAmountMinor(item) : null;
    if (charge == null || item == null) return null;
    const totalMinor = applyCatalogDigitalDeliveryDeduction(charge, item, legalEntityFulfillmentMode);
    return formatCatalogAmountMinor(totalMinor, item.currency, i18n.language);
  }, [catalogQuery.data, i18n.language, requestType, legalEntityFulfillmentMode]);

  const incomeChargeTotalLabel = useMemo((): string | null => {
    if (!incomeReviewPayload) return null;
    const item = catalogItemForType(catalogQuery.data ?? [], "income_certificate");
    const billableCount = countIncomeCertificateBillableFiles(incomeReviewPayload.uploads);
    const mode = incomeReviewPayload.fulfillmentMode;
    let totalMinor =
      estimatePerFileCertificateTotalMinor(item, billableCount, mode) ?? null;
    if (totalMinor == null && item != null) {
      const base = catalogCustomerAmountMinor(item);
      if (base != null) {
        totalMinor = applyCatalogDigitalDeliveryDeduction(base, item, mode);
      }
    }
    if (totalMinor == null || item == null) return null;
    return formatCatalogAmountMinor(totalMinor, item.currency, i18n.language);
  }, [catalogQuery.data, incomeReviewPayload, i18n.language]);

  const registrationInvoiceRutDigits = useMemo((): string | null => {
    if (user?.consumerAccountKind !== "company" || !user.consumerCompanyRegistration?.rut) {
      return null;
    }
    const d = user.consumerCompanyRegistration.rut.replace(/\D/g, "").slice(0, 12);
    return d.length === 12 ? d : null;
  }, [user?.consumerAccountKind, user?.consumerCompanyRegistration?.rut]);

  const invoiceRutLockedToRegistration = registrationInvoiceRutDigits != null;

  const effectiveLegalEntityInvoiceIncludeRut = invoiceRutLockedToRegistration
    ? true
    : legalEntityInvoiceIncludeRut;
  const effectiveLegalEntityInvoiceRutDigits = invoiceRutLockedToRegistration
    ? registrationInvoiceRutDigits!
    : legalEntityInvoiceRutDigits;

  const effectiveIncomeInvoiceIncludeRut = invoiceRutLockedToRegistration
    ? true
    : incomeInvoiceIncludeRut;
  const effectiveIncomeInvoiceRutDigits = invoiceRutLockedToRegistration
    ? registrationInvoiceRutDigits!
    : incomeInvoiceRutDigits;

  const legalEntityInvoiceValid =
    !effectiveLegalEntityInvoiceIncludeRut ||
    effectiveLegalEntityInvoiceRutDigits.replace(/\D/g, "").length === 12;

  const incomeInvoiceValid =
    !effectiveIncomeInvoiceIncludeRut ||
    effectiveIncomeInvoiceRutDigits.replace(/\D/g, "").length === 12;

  const notifyPaymentError = useCallback((err: unknown): void => {
    void err;
    toast.error(t("requests.user.paymentCheckoutError"));
  }, [t]);

  useEffect(() => {
    if (step !== "incomeCertificate") {
      setIncomePrimaryEnabled(false);
      setIncomeReviewPhase("wizard");
      setIncomeReviewPayload(null);
      setIncomeInvoiceIncludeRut(false);
      setIncomeInvoiceRutDigits("");
      setIncomeInnerStep(1);
      incomeForm.reset();
    }
  }, [step, incomeForm.reset]);

  useEffect(() => {
    if (step !== "legalEntityDetails") {
      setLegalEntityInvoiceIncludeRut(false);
      setLegalEntityInvoiceRutDigits("");
      setLegalEntityCompanyRutMode("other");
    }
  }, [step]);

  const createMutation = useMutation({
    mutationFn: createMyNotarialRequest,
    onError: () => {
      toast.error(t("requests.user.createError"));
    },
  });

  const createAccountantMutation = useMutation({
    mutationFn: createMyAccountantRequest,
    onError: () => {
      toast.error(t("requests.user.createError"));
    },
  });

  // Moderation refuses an RFQ whose title or description carries contact
  // details. Kept as state rather than a toast because the copy asks the
  // consumer to rewrite the text — advice that has to still be on screen while
  // they do it. This is the widest-reach channel on the platform: a request
  // goes to every professional in the discipline at once.
  const [quoteRequestBlockedKinds, setQuoteRequestBlockedKinds] = useState<
    ContactDetailKind[] | null
  >(null);

  const createQuoteRequestMutation = useMutation({
    mutationFn: createQuoteRequest,
    onSuccess: () => {
      setQuoteRequestBlockedKinds(null);
    },
    onError: (err: unknown) => {
      const kinds = contactDetailKindsFromError(err);
      if (kinds) {
        setQuoteRequestBlockedKinds(kinds);
        return;
      }
      setQuoteRequestBlockedKinds(null);
      toast.error(t("requests.user.createError"));
    },
  });

  const clearLegalEntityDocuments = useCallback((): void => {
    setEstatutoFile(null);
    setComunicacionBcuFile(null);
    setLegalDocumentType("national_id");
    setLegalDocumentFrontFile(null);
    setLegalDocumentBackFile(null);
    setPassportDocumentFile(null);
  }, []);

  const resetLegalEntityFiles = useCallback((): void => {
    setValidatedCompany(null);
    setLegalEntityManualMode(false);
    setLegalEntityManualFields(EMPTY_MANUAL_COMPANY_FIELDS);
    clearLegalEntityDocuments();
  }, [clearLegalEntityDocuments]);

  const handleIntentChange = useCallback((value: string): void => {
    const slug = value.trim() !== "" ? value : null;
    setSelectedIntentSlug(slug);
    setSelectedEnterpriseProduct(null);
    if (slug && certificateRequestIntentsQuery.data) {
      const row = certificateRequestIntentsQuery.data.find((r) => r.slug === slug);
      if (row?.status === "active") {
        if (row.flowKey === CERTIFICATE_CATALOG_FLOW_KEY) {
          setRequestType(null);
          setCategory(null);
          setStep("chooseType");
        } else if (row.flowKey === ENTERPRISE_CATALOG_FLOW_KEY) {
          setRequestType(null);
          setCategory(null);
          setStep("chooseEnterpriseProduct");
        } else if (row.flowKey === "quote_request") {
          setRequestType("quote_request");
          setStep("quoteRequest");
        }
      }
    }
  }, [certificateRequestIntentsQuery.data]);

  /**
   * Deep link straight to a catalog product — now reachable ONLY from an
   * enterprise workflow node (spec `retire-fixed-certificates-for-rfq`).
   *
   * It used to be fed by `?type=`, the consumer-facing marketing link. That
   * flow is retired: `/requests/new` goes to the intake chat and `?type=` is
   * stripped on arrival, so the only caller left is a graph node spawning a
   * child certificate request, which passes
   * `?requestType=<product>&returnEnterpriseRequest=<id>`.
   *
   * The caller gates on `returnEnterpriseRequest` being present, so the
   * retirement cannot be undone by typing a URL: a bare
   * `?requestType=income_certificate` reaches nothing.
   *
   * The intent it selects is NOT required to be active any more. Hiding the
   * `certificates` card is exactly how the consumer entry point was retired,
   * and requiring `active` here would have made the enterprise path die with
   * it — the node would land on the picker with no card to pick.
   *
   * Lands on chooseType with the product SELECTED rather than skipping ahead:
   * each product has its own downstream flow (income wizard, legal-entity
   * flow, or a direct create), and reproducing that routing here would be a
   * second copy of it that drifts.
   */
  const deepLinkAppliedRef = useRef(false);
  /**
   * handleCertificateTypeChange is defined below this effect and depends on
   * catalog state, so it is reached through a ref rather than by hoisting the
   * callback — hoisting it would put the deep-link plumbing in the middle of
   * the product-selection logic, which is not what that function is about.
   */
  const handleCertificateTypeChangeRef = useRef<((value: string) => void) | null>(null);
  useEffect(() => {
    if (deepLinkAppliedRef.current) return;
    const wantedRaw = initialWizardType?.trim();
    const wanted = wantedRaw && wantedRaw !== "generic" ? wantedRaw : null;
    if (!wanted && !enterpriseCatalogEntry) return;
    const intents = certificateRequestIntentsQuery.data;
    const catalog = catalogQuery.data;
    // Both are needed to resolve the link; retry on the next render until they
    // land rather than giving up on the first pass.
    if (!intents || !catalog) return;

    // An enterprise node that named no product: open the catalog and let the
    // visitor choose, rather than leaving them on a picker whose only card is
    // the RFQ.
    if (!wanted) {
      const catalogIntent = intents.find((row) => row.flowKey === CERTIFICATE_CATALOG_FLOW_KEY);
      deepLinkAppliedRef.current = true;
      if (!catalogIntent) return;
      setSelectedIntentSlug(catalogIntent.slug);
      setRequestType(null);
      setCategory(null);
      setStep("chooseType");
      return;
    }

    // An unknown or withdrawn product leaves the wizard exactly as it was, on
    // the intent picker. A stale marketing link should degrade to the normal
    // entry, never to an error or a half-configured wizard.
    const product = catalog.find((item) => item.targetType === wanted);
    // Status deliberately not checked — see the note above.
    const intent = intents.find((row) => row.flowKey === CERTIFICATE_CATALOG_FLOW_KEY);
    deepLinkAppliedRef.current = true;
    if (!product || !intent) return;

    setSelectedIntentSlug(intent.slug);
    handleCertificateTypeChangeRef.current?.(product.targetType);
    setStep("chooseType");
  }, [
    initialWizardType,
    enterpriseCatalogEntry,
    certificateRequestIntentsQuery.data,
    catalogQuery.data,
  ]);

  const handleCertificateTypeChange = useCallback(
    (value: string): void => {
      const nextCategory = categoryForCatalogProductType(value, catalogQuery.data);
      if (category !== nextCategory) {
        setValidatedCompany(null);
        setRut("");
        resetLegalEntityFiles();
      }
      setLegalEntityInvoiceIncludeRut(false);
      setLegalEntityInvoiceRutDigits("");
      setIncomeInvoiceIncludeRut(false);
      setIncomeInvoiceRutDigits("");
      setRequestType(value);
      setCategory(nextCategory);
    },
    [catalogQuery.data, category, resetLegalEntityFiles],
  );
  handleCertificateTypeChangeRef.current = handleCertificateTypeChange;

  const handleRutInputChange = useCallback(
    (digits: string): void => {
      setRut(digits);
      setValidatedCompany(null);
      setLegalEntityPhase("form");
      resetLegalEntityFiles();
    },
    [resetLegalEntityFiles],
  );

  const isLegalEntityFlow = requestType === "notarial_legal_entity" && step === "legalEntityDetails";
  const isQuoteRequest = step === "quoteRequest";
  const isIncomeCertificateFlow = step === "incomeCertificate";
  const isChooseIntent = step === "chooseIntent";
  const isChooseType = step === "chooseType";
  const isChooseEnterpriseProduct = step === "chooseEnterpriseProduct";
  const isPreconditionCheck = step === "preconditionCheck";
  const isFillEnterpriseProduct = step === "fillEnterpriseProduct";
  const isWorkflowSteps = step === "workflowSteps";

  function resolveNextStepAfterSelection(entry: EnterpriseCatalogEntry): void {
    // If a request for this product already exists, resume it
    if (activeEnterpriseRequest && activeEnterpriseRequest.productSlug === entry.slug) {
      setStep("workflowSteps");
      return;
    }

    // Don't create the request yet — go straight to the workflow steps.
    // The request will be created when the user completes the first
    // interactive step (handleCompleteWorkflowStep detects no active request).
    setStep("workflowSteps");
  }

  const handleSelectEnterpriseProduct = useCallback((entry: EnterpriseCatalogEntry): void => {
    setSelectedEnterpriseProduct(entry);
    resolveNextStepAfterSelection(entry);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCompleteWorkflowStep = useCallback(async (stepKey: string, stepData: Record<string, unknown>): Promise<void> => {
    // If viewing a previous node (back navigation), just return to the live node
    if (workflowViewNodeId) {
      setWorkflowViewNodeId(null);
      return;
    }

    let request = activeEnterpriseRequest;

    // If request already exists, complete the step server-side
    if (request) {
      const updated = await completeMyEnterpriseRequestStep(request.id, stepKey, stepData);
      void queryClient.invalidateQueries({ queryKey: ["me", "enterprise-requests"] });

      const state = updated.workflowState as import("@gestionalo/enterprise-graph").GraphState | null;
      if (state?.currentNodeId && selectedEnterpriseProduct?.graph) {
        const nextNode = selectedEnterpriseProduct.graph.nodes.find(
          (n: { id: string }) => n.id === state.currentNodeId,
        );
        if (nextNode && nextNode.type === "internalRequest") {
          navigate(`/requests/${updated.id}?source=enterprise`, { replace: true });
          return;
        }
      }
      setActiveEnterpriseRequest(updated);
      return;
    }

    // No request yet — accumulate step data client-side
    const allPending = { ...pendingStepData, [stepKey]: stepData };
    setPendingStepData(allPending);

    // Check what the NEXT node will be after this step. If it requires a
    // server-side request (inviteActors, internalRequest, staffAction, staffApproval),
    // create the request NOW with all accumulated data.
    const graph = selectedEnterpriseProduct?.graph;
    if (!graph) return;

    // Find the next interactive node by walking the graph from the current node
    const currentNodeObj = graph.nodes.find((n: { id: string }) => n.id === stepKey);
    if (!currentNodeObj) return;

    // Walk forward past automatic nodes to find the next interactive node
    const outEdges = graph.edges.filter((e: { source: string }) => e.source === stepKey);
    let nextNodeId = outEdges.length === 1 ? outEdges[0].target : null;

    // For precondition nodes, follow the 'pass' edge
    if (currentNodeObj.type === 'precondition') {
      const passEdge = outEdges.find((e: { sourceHandle?: string }) => e.sourceHandle === 'pass');
      nextNodeId = passEdge?.target ?? null;
    }

    // For decision nodes, follow the edge matching the chosen value
    if (currentNodeObj.type === 'decision' && outEdges.length > 1) {
      const decisionData = currentNodeObj.data as { outputKey: string; options?: Array<{ value: string }> };
      const chosenValue = stepData[decisionData.outputKey] as string | undefined;
      if (chosenValue) {
        const chosenEdge = outEdges.find((e: { sourceHandle?: string }) => e.sourceHandle === chosenValue);
        nextNodeId = chosenEdge?.target ?? null;
      }
    }

    // Walk past automatic nodes (branch, pricing), evaluating branch conditions
    const AUTO_TYPES = new Set(['branch', 'pricing']);
    while (nextNodeId) {
      const nextNode = graph.nodes.find((n: { id: string }) => n.id === nextNodeId);
      if (!nextNode || !AUTO_TYPES.has(nextNode.type)) break;
      const nextEdges = graph.edges.filter((e: { source: string }) => e.source === nextNodeId);

      if (nextNode.type === 'branch' && nextEdges.length > 1) {
        // Evaluate branch condition against accumulated values
        const allFlat: Record<string, unknown> = {};
        for (const stepValues of Object.values(allPending)) {
          Object.assign(allFlat, stepValues);
        }
        const branchData = nextNode.data as { field: string; cases?: Array<{ id: string; op: string; value: string | number; label?: string }> };
        const fieldValue = allFlat[branchData.field];
        const matchedCase = (branchData.cases ?? []).find((c) => {
          const fv = Number(fieldValue);
          const cv = Number(c.value);
          switch (c.op) {
            case 'equals': return String(fieldValue) === String(c.value);
            case 'gt': return fv > cv;
            case 'gte': return fv >= cv;
            case 'lt': return fv < cv;
            case 'lte': return fv <= cv;
            default: return false;
          }
        });
        if (matchedCase) {
          const matched = nextEdges.find((e: { sourceHandle?: string; data?: { caseId?: string } }) =>
            e.sourceHandle === matchedCase.id || e.data?.caseId === matchedCase.id);
          nextNodeId = matched?.target ?? null;
        } else {
          const defaultEdge = nextEdges.find((e: { sourceHandle?: string; data?: { isDefault?: boolean } }) =>
            e.sourceHandle === 'default' || e.data?.isDefault);
          nextNodeId = defaultEdge?.target ?? null;
        }
      } else {
        nextNodeId = nextEdges.length === 1 ? nextEdges[0].target : null;
      }
    }

    const nextNode = nextNodeId ? graph.nodes.find((n: { id: string }) => n.id === nextNodeId) : null;
    const SERVER_REQUIRED_TYPES = new Set(['inviteActors', 'internalRequest', 'staffApproval', 'staffAction']);
    const isCreateRequestNode = nextNode && nextNode.type === 'createRequest';
    const needsServerNow = nextNode && (SERVER_REQUIRED_TYPES.has(nextNode.type) || isCreateRequestNode);

    if (needsServerNow) {
      const product = selectedEnterpriseProduct;
      if (!product) {
        toast.error(t("requests.user.wizard.enterpriseFormError"));
        throw new Error('CREATION_FAILED');
      }
      // Create the request with all accumulated data
      setEnterpriseFormSubmitting(true);
      try {
        const existing = await listMyEnterpriseRequests();
        const pendingForProduct = existing.find(
          (r) => r.productSlug === product.slug && r.status === 'pending',
        );
        if (pendingForProduct) {
          request = pendingForProduct;
        } else {
          request = await createEnterpriseRequest(product.slug, allPending);
        }
        void queryClient.invalidateQueries({ queryKey: ["me", "enterprise-requests"] });
      } catch (err) {
        setEnterpriseFormSubmitting(false);
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message
          ?? t("requests.user.wizard.enterpriseFormError");
        toast.error(message);
        throw new Error('CREATION_FAILED');
      }

      setActiveEnterpriseRequest(request);
      setPendingStepData({});

      toast.success(t("requests.user.wizard.enterpriseRequestCreated"));

      // createRequest node: always redirect to the request detail page
      if (isCreateRequestNode) {
        navigate(`/requests/${request.id}?source=enterprise`, { replace: true });
        setEnterpriseFormSubmitting(false);
        return;
      }

      const newState = request.workflowState as import("@gestionalo/enterprise-graph").GraphState | null;
      if (newState?.currentNodeId && selectedEnterpriseProduct?.graph) {
        const nn = selectedEnterpriseProduct.graph.nodes.find(
          (n: { id: string }) => n.id === newState.currentNodeId,
        );
        if (nn && nn.type === "internalRequest") {
          navigate(`/requests/${request.id}?source=enterprise`, { replace: true });
          setEnterpriseFormSubmitting(false);
          return;
        }
      }
      setEnterpriseFormSubmitting(false);
      return;
    }

    // Next node is another form/precondition — advance client-side state
    // The wizard page's initialGraphState will re-compute to show the next node
  }, [activeEnterpriseRequest, pendingStepData, workflowViewNodeId, queryClient, selectedEnterpriseProduct, navigate, t]);

  const isWizardSubmitting =
    isPaymentResumeRunning ||
    isCertificatePaymentBusy ||
    createMutation.isPending ||
    createAccountantMutation.isPending ||
    createQuoteRequestMutation.isPending ||
    incomeFlowPhase !== "idle";

  const legalEntityEffectiveCompany = useMemo((): ValidateRutResponse | null => {
    if (validatedCompany != null) return validatedCompany;
    if (
      legalEntityManualMode &&
      legalEntityManualFields.legalName.trim() !== ""
    ) {
      return manualCompanyFieldsToValidateRutResponse(legalEntityManualFields);
    }
    return null;
  }, [validatedCompany, legalEntityManualMode, legalEntityManualFields]);

  const updateLegalEntityManualField = useCallback(
    (field: keyof ManualCompanyFieldsState, value: string): void => {
      setLegalEntityManualFields((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const canSubmitLegalEntity =
    requestType === "notarial_legal_entity" &&
    legalEntityEffectiveCompany != null &&
    estatutoFile != null &&
    comunicacionBcuFile != null &&
    (legalDocumentType === "national_id"
      ? legalDocumentFrontFile != null && legalDocumentBackFile != null
      : passportDocumentFile != null);

  const canContinueIntent = useMemo((): boolean => {
    if (!selectedIntentSlug || !certificateRequestIntentsQuery.data) return false;
    const row = certificateRequestIntentsQuery.data.find((r) => r.slug === selectedIntentSlug);
    return (
      row != null &&
      row.status === "active" &&
      (row.flowKey === CERTIFICATE_CATALOG_FLOW_KEY || row.flowKey === ENTERPRISE_CATALOG_FLOW_KEY)
    );
  }, [selectedIntentSlug, certificateRequestIntentsQuery.data]);
  const canContinueType = requestType != null;

  const goBackToTypeOrCategory = useCallback((): void => {
    setIncomeReviewPhase("wizard");
    setIncomeReviewPayload(null);
    if (!category) {
      setStep("chooseIntent");
      setSelectedIntentSlug(null);
      setRequestType(null);
      return;
    }
    const types = typesForCategory(category);
    if (types.length > 0) {
      // Must clear requestType — the chooseType screen auto-advances when requestType is non-null,
      // which otherwise bounces the user straight back into the flow they just came out of.
      setRequestType(null);
      setStep("chooseType");
      return;
    }
    setStep("chooseIntent");
    setCategory(null);
    setRequestType(null);
    setSelectedIntentSlug(null);
  }, [category]);

  const submitSimpleNotaryRequest = useCallback(
    async (type: string): Promise<void> => {
      setIsCertificatePaymentBusy(true);
      try {
        const checkoutRow = await createCertificateCheckout(
          "notary",
          type,
          undefined,
          undefined,
          paymentMethod,
        );
        toastIfCheckoutAutoPaidBypass(checkoutRow);
        if (isCheckoutAutoPaidBypass(checkoutRow)) {
          const created = await createMutation.mutateAsync({
            requestType: type,
            checkoutId: checkoutRow.checkoutId,
          });
          void queryClient.invalidateQueries({ queryKey: ["me", "certificate-requests"] });
          void queryClient.invalidateQueries({ queryKey: ["me", "notarial-requests"] });
          toast.success(t("requests.user.createSuccess"));
          onRequestCreated(created.id, "notarial");
          return;
        }
        await putCertificatePaymentResume({
          checkoutId: checkoutRow.checkoutId,
          version: 1,
          kind: "simple_notary",
          requestType: type,
        });
        redirectToCertificateCheckout(checkoutRow, { serviceType: type });
      } catch (err) {
        notifyPaymentError(err);
      } finally {
        setIsCertificatePaymentBusy(false);
      }
    },
    [createMutation, notifyPaymentError, onRequestCreated, paymentMethod, queryClient, t],
  );

  const submitSimpleAccountantRequest = useCallback(
    async (type: string): Promise<void> => {
      setIsCertificatePaymentBusy(true);
      try {
        const checkoutRow = await createCertificateCheckout(
          "accountant",
          type,
          undefined,
          undefined,
          paymentMethod,
        );
        toastIfCheckoutAutoPaidBypass(checkoutRow);
        if (isCheckoutAutoPaidBypass(checkoutRow)) {
          const created = await createAccountantMutation.mutateAsync({
            type,
            checkoutId: checkoutRow.checkoutId,
          });
          void queryClient.invalidateQueries({ queryKey: ["me", "certificate-requests"] });
          void queryClient.invalidateQueries({ queryKey: ["me", "accountant-requests"] });
          toast.success(t("requests.user.createSuccess"));
          onRequestCreated(created.id, "accountant");
          return;
        }
        await putCertificatePaymentResume({
          checkoutId: checkoutRow.checkoutId,
          version: 1,
          kind: "simple_accountant",
          requestType: type,
        });
        redirectToCertificateCheckout(checkoutRow, { serviceType: type });
      } catch (err) {
        notifyPaymentError(err);
      } finally {
        setIsCertificatePaymentBusy(false);
      }
    },
    [createAccountantMutation, notifyPaymentError, onRequestCreated, paymentMethod, queryClient, t],
  );

  const advanceAfterTypeSelected = useCallback(
    (type: string): void => {
      const kind = flowKindForProductType(type, catalogQuery.data);
      if (kind === "legal_entity_notary") {
        setLegalEntityPhase("form");
        setRut("");
        resetLegalEntityFiles();
        const snap = user?.consumerAccountKind === "company" ? user.consumerCompanyRegistration : null;
        const digits = snap?.rut?.replace(/\D/g, "").slice(0, 12) ?? "";
        setLegalEntityCompanyRutMode(digits.length === 12 ? "choose" : "other");
        setStep("legalEntityDetails");
        return;
      }
      if (kind === "income_accountant") {
        setIncomeReviewPhase("wizard");
        setIncomeReviewPayload(null);
        setStep("incomeCertificate");
        return;
      }
      if (kind === "simple_accountant") {
        void submitSimpleAccountantRequest(type);
        return;
      }
      void submitSimpleNotaryRequest(type);
    },
    [catalogQuery.data, resetLegalEntityFiles, submitSimpleAccountantRequest, submitSimpleNotaryRequest, user],
  );

  const selectLegalEntitySessionCompany = useCallback((): void => {
    const snap = user?.consumerCompanyRegistration;
    if (user?.consumerAccountKind !== "company" || !snap) return;
    const digits = snap.rut.replace(/\D/g, "").slice(0, 12);
    if (digits.length !== 12) return;
    setLegalEntityCompanyRutMode("session");
    setRut(digits);
    setValidatedCompany(registrationSnapshotToValidateRutResponse(snap));
    setLegalEntityManualMode(!snap.dgiValidated);
    setLegalEntityManualFields(EMPTY_MANUAL_COMPANY_FIELDS);
    setLegalEntityPhase("form");
  }, [user?.consumerAccountKind, user?.consumerCompanyRegistration]);

  const selectLegalEntityOtherCompany = useCallback((): void => {
    setLegalEntityCompanyRutMode("other");
    setRut("");
    resetLegalEntityFiles();
  }, [resetLegalEntityFiles]);

  const returnToLegalEntityCompanyChoice = useCallback((): void => {
    setLegalEntityCompanyRutMode("choose");
    setRut("");
    resetLegalEntityFiles();
  }, [resetLegalEntityFiles]);

  const handleContinueFromIntent = useCallback((): void => {
    if (!canContinueIntent || !selectedIntentSlug || !certificateRequestIntentsQuery.data) return;
    const row = certificateRequestIntentsQuery.data.find((r) => r.slug === selectedIntentSlug);
    setRequestType(null);
    setCategory(null);
    setSelectedEnterpriseProduct(null);
    if (row?.flowKey === ENTERPRISE_CATALOG_FLOW_KEY) {
      setStep("chooseEnterpriseProduct");
    } else {
      setStep("chooseType");
    }
  }, [canContinueIntent, selectedIntentSlug, certificateRequestIntentsQuery.data]);

  const handleContinueFromType = useCallback((): void => {
    if (requestType == null) return;
    advanceAfterTypeSelected(requestType);
  }, [advanceAfterTypeSelected, requestType]);

  const handleValidateRut = useCallback(async (): Promise<void> => {
    const digits = rut.replace(/\D/g, "");
    if (digits.length !== 12) {
      toast.error(t("auth.register.rutFormatError"));
      return;
    }
    setIsValidatingRut(true);
    try {
      const data = await validateRut(digits);
      setValidatedCompany(data);
      setLegalEntityManualMode(false);
      setLegalEntityManualFields(EMPTY_MANUAL_COMPANY_FIELDS);
      toast.success(t("requests.user.legalEntityRutValidated"));
    } catch (err) {
      setValidatedCompany(null);
      if (isValidateRutServiceUnavailableError(err)) {
        setLegalEntityManualMode(true);
        toast.error(t("requests.user.rutValidationUnavailable"));
      } else {
        setLegalEntityManualMode(false);
        toast.error(t("requests.user.legalEntityRutInvalid"));
      }
    } finally {
      setIsValidatingRut(false);
    }
  }, [rut, t]);

  const handleContinueLegalEntityToSummary = useCallback((): void => {
    if (requestType !== "notarial_legal_entity" || step !== "legalEntityDetails") return;
    if (!canSubmitLegalEntity) return;
    setLegalEntityPhase("summary");
  }, [canSubmitLegalEntity, requestType, step]);

  const handleLegalEntityFooterPrimary = useCallback(async (): Promise<void> => {
    if (requestType !== "notarial_legal_entity" || step !== "legalEntityDetails") return;
    if (legalEntityPhase !== "summary") return;
    if (!canSubmitLegalEntity) return;
    if (legalEntityEffectiveCompany == null || estatutoFile == null || comunicacionBcuFile == null) return;

    setIsCertificatePaymentBusy(true);
    try {
      const checkoutRow = await createCertificateCheckout(
        "notary",
        "notarial_legal_entity",
        undefined,
        legalEntityFulfillmentMode,
        paymentMethod,
      );
      toastIfCheckoutAutoPaidBypass(checkoutRow);
      if (isCheckoutAutoPaidBypass(checkoutRow)) {
        try {
          const co = legalEntityEffectiveCompany;
          const created = await createMutation.mutateAsync({
            requestType: "notarial_legal_entity",
            checkoutId: checkoutRow.checkoutId,
            fulfillmentMode: legalEntityFulfillmentMode,
            companySnapshot: {
              legalName: co.legalName,
              rut: rut.replace(/\D/g, "").slice(0, 12),
              legalAddress: co.legalAddress,
              city: co.city,
              department: co.department,
              phone: co.phone,
              email: co.email,
            },
            invoiceIncludeRut: effectiveLegalEntityInvoiceIncludeRut,
            invoiceRutDigits: effectiveLegalEntityInvoiceIncludeRut
              ? effectiveLegalEntityInvoiceRutDigits.replace(/\D/g, "").slice(0, 12)
              : undefined,
            companyRutUnverified: legalEntityManualMode === true,
          });
          const uploads: Array<[UserNotarialRequestDocumentType, File | null]> = [
            ["estatuto", estatutoFile],
            ["comunicacion_bcu", comunicacionBcuFile],
            ...(legalDocumentType === "national_id"
              ? ([
                  ["legal_document_front", legalDocumentFrontFile],
                  ["legal_document_back", legalDocumentBackFile],
                ] as Array<[UserNotarialRequestDocumentType, File | null]>)
              : ([["passport_document", passportDocumentFile]] as Array<
                  [UserNotarialRequestDocumentType, File | null]
                >)),
          ];
          await Promise.all(
            uploads
              .filter(([, file]) => file != null)
              .map(([docType, file]) =>
                uploadMyNotarialRequestDocument(created.id, docType, file as File),
              ),
          );
          await queryClient.invalidateQueries({ queryKey: ["me", "certificate-requests"] });
          await queryClient.invalidateQueries({ queryKey: ["me", "notarial-requests"] });
          await queryClient.invalidateQueries({ queryKey: ["me", "notarial-requests", created.id] });
          toast.success(t("requests.user.createSuccess"));
          onRequestCreated(created.id, "notarial");
        } catch {
          if (!createMutation.isError) {
            toast.error(t("requests.user.legalEntityUploadError"));
          }
        }
        return;
      }
      const resume = await buildLegalEntityPaymentResume({
        checkoutId: checkoutRow.checkoutId,
        fulfillmentMode: legalEntityFulfillmentMode,
        rut,
        validatedCompany: legalEntityEffectiveCompany,
        companyRutUnverified: legalEntityManualMode === true,
        legalDocumentType,
        estatutoFile,
        comunicacionBcuFile,
        legalDocumentFrontFile,
        legalDocumentBackFile,
        passportDocumentFile,
        invoiceIncludeRut: effectiveLegalEntityInvoiceIncludeRut,
        invoiceRutDigits: effectiveLegalEntityInvoiceIncludeRut
          ? effectiveLegalEntityInvoiceRutDigits.replace(/\D/g, "").slice(0, 12)
          : null,
      });
      await putCertificatePaymentResume(resume);
      redirectToCertificateCheckout(checkoutRow, { serviceType: "notarial_legal_entity" });
    } catch (err) {
      notifyPaymentError(err);
    } finally {
      setIsCertificatePaymentBusy(false);
    }
  }, [
    canSubmitLegalEntity,
    comunicacionBcuFile,
    createMutation,
    paymentMethod,
    estatutoFile,
    legalDocumentBackFile,
    legalDocumentFrontFile,
    legalDocumentType,
    legalEntityEffectiveCompany,
    legalEntityManualMode,
    legalEntityPhase,
    notifyPaymentError,
    onRequestCreated,
    passportDocumentFile,
    queryClient,
    requestType,
    rut,
    step,
    t,
    legalEntityFulfillmentMode,
    effectiveLegalEntityInvoiceIncludeRut,
    effectiveLegalEntityInvoiceRutDigits,
  ]);

  /**
   * Drop out of the conversation into the static form.
   *
   * Called when the model is unavailable (503) and when the consumer opts out.
   * Whatever the chat had already established is carried across rather than
   * discarded — being handed an empty form after answering three questions is
   * the worst version of this fallback.
   */
  const fallbackToQuoteRequestForm = useCallback((seed?: { title?: string; description?: string }): void => {
    if (seed?.title) setQuoteRequestTitle(seed.title);
    if (seed?.description) setQuoteRequestDescription(seed.description);
    setQuoteIntakeThinking(false);
    // No draft came out of the conversation, so the request must go through the
    // one-shot classifier -- carrying the id would fail the submit outright.
    setQuoteIntakeDraftConversationId(null);
    setQuoteAnticipatedDocuments([]);
    // The items are gone, so their answers have nothing left to attach to.
    setQuoteIntakeAnswers({});
    setQuoteDraftDisciplines([]);
    setQuoteRequestPhase("form");
  }, []);

  /**
   * Out of scope, but send it anyway.
   *
   * A false out_of_scope turns away real business, which is strictly worse
   * than routing slowly — and it happens: the assistant told a consumer that
   * registering a domestic employee with BPS was neither notarial nor
   * accounting work, which is a contador's job. Offering only "escribinos"
   * meant the request died there, and in an environment without the chat
   * widget the button did nothing at all.
   *
   * So the classifier's verdict is a suggestion here too. The consumer drops
   * into the same manual form the exhausted-turns path uses, carrying what
   * they already said, and a person triages it.
   */
  const sendOutOfScopeAnyway = useCallback((): void => {
    const said = quoteIntakeMessages
      .filter((m) => m.role === "consumer")
      .map((m) => m.content);
    setQuoteIntakeOutOfScopeMessage(null);
    fallbackToQuoteRequestForm({
      title: said[0]?.slice(0, 120),
      description: said.join("\n\n"),
    });
  }, [quoteIntakeMessages, fallbackToQuoteRequestForm]);

  /**
   * Move a drafted conversation into the editable form.
   *
   * ONE APPLIER, TWO CALLERS: the live turn that produced the draft, and a
   * refresh that rehydrates one from the URL. These were a single inline block
   * until the resume path existed; duplicating it would let a reloaded intake
   * carry a different set of fields than the one that never reloaded, and the
   * ones that would silently go missing — producesPhysicalDocument, the
   * disciplines — are the ones that decide which steps the consumer is asked
   * for next.
   *
   * Straight into the editable form rather than a bespoke confirmation screen:
   * the consumer must see and be able to correct what the professionals will
   * price against, and these fields already do that.
   */
  const applyIntakeDraft = useCallback(
    (draft: QuoteIntakeDraft, conversationId: string): boolean => {
      if (!draft.title || !draft.summary) return false;
      setQuoteRequestTitle(draft.title);
      setQuoteRequestDescription(draft.summary);
      // Carries the conversation's own classification through to the submit,
      // so the backend skips the one-shot classifier entirely.
      setQuoteIntakeDraftConversationId(conversationId);
      setQuoteAnticipatedDocuments(draft.anticipatedDocuments ?? []);
      // The draft's `producesPhysicalDocument` is deliberately NOT read here
      // any more (spec f462e7d6 3.2). It gated the wizard's delivery step,
      // which is now shown to everyone; the backend still records what triage
      // decided, and the professional's own flag still drives the fee and the
      // child request's fulfilment mode. Nothing in the consumer wizard
      // branches on a triage guess.
      setQuoteDraftDisciplines(
        [draft.discipline, draft.alsoNeeds].filter(
          (d): d is "notary" | "accountant" => d === "notary" || d === "accountant",
        ),
      );
      setQuoteRequestPhase("form");
      return true;
    },
    [],
  );

  /**
   * Resume the conversation named in the URL.
   *
   * WITHOUT THIS A REFRESH IS SILENT DATA LOSS. The conversation was always
   * persisted server-side; the client simply had no handle to ask for one
   * back, so a reload dropped the consumer on "¿Qué necesitás?" with no sign
   * that three answered questions had gone. It also covers the login
   * round-trip, which is the same bug wearing a different hat: type two
   * messages, meet the auth gate at submit, come back to nothing.
   *
   * THE UUID IS AN IDENTIFIER, NOT A CAPABILITY. The server loads it with
   * `where: { id, startedByUserId }` and 404s otherwise, so someone else's
   * link resolves to nothing. Nothing here should be read as the id being
   * secret.
   *
   * ONCE PER MOUNT, via the ref. searchParams changes on every step mirror,
   * and re-fetching the transcript on each of those would overwrite live
   * optimistic messages with a staler server copy mid-conversation.
   *
   * A SPENT CONVERSATION IS NOT RESUMED: once it produced a quoteRequestId the
   * request already exists, so rehydrating the wizard onto it would invite a
   * duplicate. The param is dropped and the wizard starts clean.
   */
  /**
   * The id already resumed, NOT a boolean.
   *
   * A boolean plus a `cancelled` cleanup flag deadlocks under StrictMode, and
   * it deadlocks only in development, which is the worst way for it to fail:
   * mount runs the effect and starts the fetch, StrictMode's immediate cleanup
   * sets cancelled, the remount sees the flag already true and returns without
   * fetching — so the in-flight response is discarded and nothing ever
   * arrives. Production, with no double-invoke, would have worked.
   *
   * Keying on the id makes the remount a no-op instead of a blocker, and the
   * first fetch's result still applies. Setting state after a StrictMode
   * unmount is harmless in React 18, so there is no cancel flag at all.
   */
  const intakeResumedIdRef = useRef<string | null>(null);
  useEffect(() => {
    const resumeId = searchParams.get(INTAKE_CONVERSATION_PARAM);
    if (resumeId == null || resumeId.trim() === "") return;
    if (intakeResumedIdRef.current === resumeId) return;
    intakeResumedIdRef.current = resumeId;

    const dropParam = (): void => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete(INTAKE_CONVERSATION_PARAM);
          return next;
        },
        { replace: true },
      );
    };

    void (async () => {
      try {
        const convo = await getQuoteIntake(resumeId);
        if (convo.quoteRequestId != null) {
          dropParam();
          return;
        }
        // THE STEP, NOT JUST THE STATE. Restoring the transcript alone leaves
        // the wizard on `chooseIntent` — the consumer sees "¿Qué necesitás?"
        // while a fully-loaded conversation sits behind it, which looks
        // exactly like the data loss this is meant to fix. Everything the
        // intake renders is gated on step === "quoteRequest".
        setStep("quoteRequest");
        setQuoteIntakeConversationId(convo.conversationId);
        setQuoteIntakeMessages(convo.messages);
        setQuoteIntakeTurnsRemaining(convo.turnsRemaining);
        if (convo.status === "drafted" && convo.draft) {
          applyIntakeDraft(convo.draft, convo.conversationId);
        } else {
          setQuoteRequestPhase("chat");
        }
      } catch {
        // A conversation that cannot be read is not an error the consumer can
        // act on — it is someone else's id, a deleted row, or a stale link.
        // Drop the param and let them start fresh rather than showing a
        // failure for something they did not do.
        dropParam();
      }
    })();
  }, [searchParams, setSearchParams, applyIntakeDraft, setStep]);

  const handleQuoteIntakeSend = useCallback(
    /**
     * `captchaToken` arrives only on the message that opens an ANONYMOUS
     * conversation (spec b5b59843, control C-6) — the composer obtains it and
     * hands it over, because the widget has to live where the visitor can see
     * it. Every later turn goes to `me/quote-intake` with the guest token, and
     * an authenticated visitor never needs one at all.
     */
    async (content: string, captchaToken?: string): Promise<void> => {
      setQuoteIntakeThinking(true);
      // Optimistically render the consumer's own message: the turn is a whole
      // round-trip with no streaming, so without this the input clears and
      // nothing appears for seconds.
      setQuoteIntakeMessages((prev) => [
        ...prev,
        { role: "consumer", content, at: new Date().toISOString() },
      ]);
      try {
        let conversationId = quoteIntakeConversationId;
        if (!conversationId) {
          const started = await startQuoteIntake(
            captchaToken ? { captchaToken } : undefined,
          );
          conversationId = started.conversationId;
          setQuoteIntakeConversationId(conversationId);
          /*
            CLAIM THE ID BEFORE THE PARAM IS WRITTEN, or the resume effect
            below treats our own URL write as a reload and wipes the message
            being sent.

            The sequence without this: the optimistic consumer bubble is
            appended, `setSearchParams` publishes ?conversation=<id>, the
            resume effect wakes on a resumeId it has never seen and fetches —
            and `sendQuoteIntakeMessage` has not persisted yet, so the server
            transcript is still EMPTY. `setQuoteIntakeMessages(convo.messages)`
            then replaces the consumer's line with nothing, and the assistant
            reply lands on the emptied array. The visitor sees the answer to a
            question that is no longer on screen.

            Reported from dev on a suggestion chip, and it is not specific to
            chips or to guests — any first message races it. The anonymous path
            only made it reliable, because minting an identity lengthens the
            window between the param write and the send resolving.

            This is the ref the effect already keys on, set one step earlier.
            A real reload still resumes: the ref starts null on a fresh mount,
            so only the write we just made is skipped.
          */
          intakeResumedIdRef.current = conversationId;
          // THE URL IS THE HANDLE. Written on the FIRST message rather than on
          // page load: a conversation minted per visit litters the table with
          // empty rows, and the first message is the moment the consumer
          // actually commits — the same moment rfq_first_message_sent counts.
          // `replace`, so browser-back still exits the wizard rather than
          // stepping through one entry per conversation.
          setSearchParams(
            (current) => {
              const next = new URLSearchParams(current);
              next.set(INTAKE_CONVERSATION_PARAM, conversationId as string);
              return next;
            },
            { replace: true },
          );
        }
        const turn = await sendQuoteIntakeMessage(conversationId, content);
        setQuoteIntakeMessages((prev) => [
          ...prev,
          { role: "assistant", content: turn.assistantMessage, at: new Date().toISOString() },
        ]);
        setQuoteIntakeTurnsRemaining(turn.turnsRemaining);

        if (turn.status === "out_of_scope") {
          setQuoteIntakeOutOfScopeMessage(turn.assistantMessage);
        } else if (turn.status === "drafted" && turn.draft) {
          applyIntakeDraft(turn.draft, conversationId);
        }
      } catch (err) {
        if (isIntakeUnavailable(err)) {
          // Not toast.error: the static form is a designed fallback, not a
          // failure the consumer caused, and a red alert over a form that
          // works reads as "something broke, stop".
          toast(t("requests.user.quoteIntakeUnavailable"), { icon: "📝" });
          // Carry the WHOLE conversation across, not just the message that
          // failed. Someone who answered three questions must not land on a
          // form containing only their last sentence.
          const said = [
            ...quoteIntakeMessages.filter((m) => m.role === "consumer").map((m) => m.content),
            content,
          ];
          fallbackToQuoteRequestForm({
            description: said.join("\n\n"),
            // Seed the title too. Landing on a form that is half-filled, with
            // the one empty field being the first one, reads as the fallback
            // having lost something -- and it mirrors what the backend derives
            // from a transcript when the conversation runs out of turns.
            title: deriveQuoteTitle(said[0]),
          });
          return;
        }
        /*
          THE SERVER'S OWN SENTENCE WHEN IT HAS ONE, and this path is the
          reason serverMessage exists. The intake's refusals are written for
          the consumer — the per-visitor ceiling answers "Demasiadas
          conversaciones desde esta conexión. Probá de nuevo más tarde." —
          and a blanket "no pudimos procesar tu mensaje" over the top of that
          invites them to retry something that cannot succeed for an hour.

          It also cost a debugging session: the missing `'guest'` enum value
          reached the browser as the generic sentence, so the failure looked
          like the model or the network rather than an INSERT, and the cause
          was only visible in the API logs.
        */
        toast.error(serverMessage(err) ?? t("requests.user.quoteIntakeError"));
        // Roll the optimistic message back so a retry does not double it.
        setQuoteIntakeMessages((prev) => prev.slice(0, -1));
      } finally {
        setQuoteIntakeThinking(false);
      }
    },
    [quoteIntakeConversationId, quoteIntakeMessages, fallbackToQuoteRequestForm, applyIntakeDraft, setSearchParams, t],
  );

  const handleContinueQuoteRequestToSummary = useCallback((): void => {
    if (step !== "quoteRequest") return;
    if (quoteRequestTitle.trim().length === 0 || quoteRequestDescription.trim().length === 0) return;
    // UNCONDITIONAL (spec f462e7d6 3.2, decision c6ba3fa). This used to skip
    // the delivery step whenever triage said the job was digital — and that
    // gate was the live defect: triage is an AI guess made before anyone
    // qualified has read the tramite, while whether it yields a testimonio is
    // the PROFESSIONAL's call, made later at quote time. A professional
    // declaring physical on a request that skipped this step produced a
    // consumer charged a delivery fee with no address recorded anywhere.
    //
    // Asking everyone closes that by construction rather than by a rule
    // someone has to remember. The cost is real and accepted: a consumer whose
    // deliverable is a PDF still gives a postal address. Wizard completion on
    // the delivery step is the thing to watch (control C-5).
    setQuoteRequestPhase("delivery");
  }, [quoteRequestTitle, quoteRequestDescription, step]);

  const handleContinueQuoteRequestToSummaryFromDelivery = useCallback((): void => {
    if (step !== "quoteRequest") return;
    setQuoteRequestPhase("summary");
  }, [step]);

  const handleQuoteRequestSubmit = useCallback(async (): Promise<void> => {
    try {
      const payload = buildQuoteRequestPayload();
      await createQuoteRequestMutation.mutateAsync(payload);
      /**
       * THE CONVERSION, fired here and nowhere earlier: the mutation has
       * resolved, so a quote_request exists and professionals can bid on it.
       * Anything before this point is a visitor who might still leave — which
       * is exactly what `rfq_first_message_sent` was counting until today.
       *
       * AFTER the await and inside the try, so a failed submit cannot report
       * one. The invalidate and the toast below are deliberately left after it:
       * they are housekeeping, and a conversion must not depend on cache
       * bookkeeping succeeding.
       */
      trackRfqSubmitted({
        fromIntake: payload.intakeConversationId != null,
        attachmentCount: payload.attachments.length,
      });
      await queryClient.invalidateQueries({ queryKey: ["me", "certificate-requests"] });
      toast.success(t("requests.user.createSuccess"));
      navigate("/requests");
    } catch {
      // error toast already handled by createQuoteRequestMutation's onError
    }
  }, [buildQuoteRequestPayload, createQuoteRequestMutation, navigate, queryClient, t]);

  const submitIncomeCertificate = useCallback(
    async (payload: IncomeCertificateSubmitPayload): Promise<string> => {
      setIncomeFlowPhase("payment");
      try {
        const billableCount = countIncomeCertificateBillableFiles(payload.uploads);
        const checkoutRow = await createCertificateCheckout(
          "accountant",
          "income_certificate",
          billableCount,
          payload.fulfillmentMode,
          paymentMethod,
        );
        toastIfCheckoutAutoPaidBypass(checkoutRow);
        if (isCheckoutAutoPaidBypass(checkoutRow)) {
          setIncomeFlowPhase("create");
          let created: Awaited<ReturnType<typeof createMyAccountantRequest>>;
          try {
            created = await createMyAccountantRequest({
              type: "income_certificate",
              checkoutId: checkoutRow.checkoutId,
              fulfillmentMode: payload.fulfillmentMode,
              incomeCertificateDetails: payload.incomeCertificateDetails,
              companySnapshot: payload.companySnapshot,
              invoiceIncludeRut: effectiveIncomeInvoiceIncludeRut,
              invoiceRutDigits: effectiveIncomeInvoiceIncludeRut
                ? effectiveIncomeInvoiceRutDigits.replace(/\D/g, "").slice(0, 12)
                : undefined,
              companyRutUnverified: payload.companyRutUnverified === true,
            });
          } catch {
            setIncomeFlowPhase("idle");
            toast.error(t("requests.user.createError"));
            throw new Error("create failed");
          }
          try {
            // Sequential: avoids backend slot-limit races when many income_invoice files upload at once.
            for (const u of payload.uploads) {
              await uploadMyAccountantRequestDocument(created.id, u.type, u.file);
            }
          } catch {
            setIncomeFlowPhase("idle");
            toast.error(t("requests.user.incomeWizard.uploadAfterCreateError"));
            throw new Error("upload failed");
          }
          setIncomeFlowPhase("idle");
          await queryClient.invalidateQueries({ queryKey: ["me", "certificate-requests"] });
          await queryClient.invalidateQueries({ queryKey: ["me", "accountant-requests"] });
          await queryClient.invalidateQueries({ queryKey: ["me", "accountant-requests", created.id] });
          toast.success(t("requests.user.createSuccess"));
          return created.id;
        }
        const resume = await buildIncomeCertificatePaymentResume(checkoutRow.checkoutId, payload, {
          invoiceIncludeRut: effectiveIncomeInvoiceIncludeRut,
          invoiceRutDigits: effectiveIncomeInvoiceIncludeRut
            ? effectiveIncomeInvoiceRutDigits.replace(/\D/g, "").slice(0, 12)
            : null,
        });
        await putCertificatePaymentResume(resume);
        redirectToCertificateCheckout(checkoutRow, { serviceType: "income_certificate" });
        throw new Error("redirecting to payment");
      } catch (err) {
        setIncomeFlowPhase("idle");
        if (err instanceof Error && err.message === "redirecting to payment") {
          throw err;
        }
        if (err instanceof Error && (err.message === "create failed" || err.message === "upload failed")) {
          throw err;
        }
        notifyPaymentError(err);
        throw new Error("payment failed");
      }
    },
    [
      effectiveIncomeInvoiceIncludeRut,
      effectiveIncomeInvoiceRutDigits,
      notifyPaymentError,
      paymentMethod,
      queryClient,
      t,
    ],
  );

  const handleIncomeStep3ContinueToReview = useCallback((payload: IncomeCertificateSubmitPayload): void => {
    setIncomeReviewPayload(payload);
    setIncomeReviewPhase("summary");
  }, []);

  const completeIncomeCertificateFromSummary = useCallback(async (): Promise<void> => {
    if (!incomeReviewPayload) return;
    try {
      const id = await submitIncomeCertificate(incomeReviewPayload);
      onRequestCreated(id, "accountant");
    } catch {
      // Errors and toasts are handled inside submitIncomeCertificate.
    }
  }, [incomeReviewPayload, onRequestCreated, submitIncomeCertificate]);

  useEffect(() => {
    const checkoutId = paymentResumeCheckoutId?.trim() ?? null;
    if (!checkoutId) {
      setIsPaymentResumeRunning(false);
      return;
    }
    let cancelled = false;
    setIsPaymentResumeRunning(true);

    // Cache the runner promise globally per checkoutId so a StrictMode double-mount
    // (or any other re-render that re-runs this effect with the same checkoutId)
    // awaits the SAME runner instead of racing a second one. Without this the second
    // mount would call `takeCertificatePaymentResume` after the first mount already
    // consumed the IndexedDB row, fire a misleading "no pudimos recuperar" toast,
    // and navigate to /requests/new — even though the actual request was created.
    let promise = inFlightResumeRunners.get(checkoutId);
    if (!promise) {
      promise = runCertificatePaymentResumeFlow({
        checkoutId,
        // The runner ignores cancellation now — once the visitor has paid we MUST
        // complete request creation even if this component unmounts. Cleanup just
        // gates local state updates below.
        cancelled: () => false,
        navigate,
        onRequestCreated: (requestId, source) => {
          onRequestCreatedRef.current(requestId, source);
        },
        onProgress: (progress) => {
          setPaymentResumeProgress(progress);
        },
        queryClient,
        t,
      }).finally(() => {
        inFlightResumeRunners.delete(checkoutId);
      });
      inFlightResumeRunners.set(checkoutId, promise);
    }

    void promise.finally(() => {
      if (!cancelled) {
        setIsPaymentResumeRunning(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [navigate, paymentResumeCheckoutId, queryClient, t]);

  const handleHeaderBack = useCallback((): void => {
    if (isIncomeCertificateFlow) {
      if (incomeReviewPhase === "summary") {
        setIncomeReviewPhase("wizard");
        setIncomeReviewPayload(null);
        return;
      }
      if (incomeInnerStep === 3) {
        setIncomeInnerStep(2);
        return;
      }
      if (incomeInnerStep === 2) {
        setIncomeInnerStep(1);
        return;
      }
      goBackToTypeOrCategory();
      return;
    }
    if (isLegalEntityFlow) {
      if (legalEntityPhase === "summary") {
        setLegalEntityPhase("form");
        return;
      }
      goBackToTypeOrCategory();
      return;
    }
    if (isWorkflowSteps) {
      const INTERACTIVE = new Set(['userForm', 'precondition', 'staffApproval', 'staffAction', 'internalRequest', 'inviteActors', 'decision']);
      const graph = selectedEnterpriseProduct?.graph;

      if (activeEnterpriseRequest) {
        const state = activeEnterpriseRequest.workflowState as import("@gestionalo/enterprise-graph").GraphState | null;
        if (state && graph) {
          // Find the previous interactive node in completedPath
          const interactiveCompleted = state.completedPath
            .map((id) => graph.nodes.find((n: { id: string }) => n.id === id))
            .filter((n): n is NonNullable<typeof n> => n != null && INTERACTIVE.has(n.type));

          if (workflowViewNodeId) {
            // Already viewing a previous node — go back one more
            const viewIdx = interactiveCompleted.findIndex((n) => n.id === workflowViewNodeId);
            if (viewIdx > 0) {
              setWorkflowViewNodeId(interactiveCompleted[viewIdx - 1].id);
              return;
            }
            // At the first completed step — go back to product selection
            setWorkflowViewNodeId(null);
            setStep("chooseEnterpriseProduct");
            setSelectedEnterpriseProduct(null);
            setActiveEnterpriseRequest(null);
            return;
          }

          // Currently on the active node — go to the last completed interactive node
          if (interactiveCompleted.length > 0) {
            setWorkflowViewNodeId(interactiveCompleted[interactiveCompleted.length - 1].id);
            return;
          }
        }
      } else if (graph) {
        // Deferred creation: no server-side request yet, use pendingStepData to navigate back
        const pendingNodeIds = Object.keys(pendingStepData);
        const interactiveCompleted = pendingNodeIds
          .map((id) => graph.nodes.find((n: { id: string }) => n.id === id))
          .filter((n): n is NonNullable<typeof n> => n != null && INTERACTIVE.has(n.type));

        if (interactiveCompleted.length > 0) {
          // Remove the last pending step to go back
          const lastNode = interactiveCompleted[interactiveCompleted.length - 1];
          setPendingStepData((prev) => {
            const next = { ...prev };
            delete next[lastNode.id];
            return next;
          });
          return;
        }
      }

      // No request or no completed steps — go back to product selection
      setStep("chooseEnterpriseProduct");
      setSelectedEnterpriseProduct(null);
      setActiveEnterpriseRequest(null);
      return;
    }
    if (isFillEnterpriseProduct) {
      setStep("chooseEnterpriseProduct");
      setSelectedEnterpriseProduct(null);
      return;
    }
    if (isPreconditionCheck) {
      setStep("chooseEnterpriseProduct");
      setSelectedEnterpriseProduct(null);
      return;
    }
    if (isChooseEnterpriseProduct) {
      setStep("chooseIntent");
      setSelectedEnterpriseProduct(null);
      setSelectedIntentSlug(null);
      return;
    }
    if (isChooseType) {
      setStep("chooseIntent");
      setRequestType(null);
      setCategory(null);
      setSelectedIntentSlug(null);
      return;
    }
    if (isQuoteRequest) {
      if (quoteRequestPhase === "summary") {
        // Delivery is always the step before the summary now, so back is
        // unconditional too — the forward path has no skip left to mirror.
        setQuoteRequestPhase("delivery");
        return;
      }
      if (quoteRequestPhase === "delivery") {
        setQuoteRequestPhase("form");
        return;
      }
      setStep("chooseIntent");
      setSelectedIntentSlug(null);
      setQuoteRequestTitle("");
      setQuoteRequestDescription("");
      setQuoteRequestAttachments([]);
      // Leaving the step abandons the conversation too. Without this, coming
      // back lands on an empty form (the phase never returned to "chat") and
      // still carries the old transcript and its draft id.
      setQuoteRequestPhase("chat");
      setQuoteIntakeConversationId(null);
      setQuoteIntakeDraftConversationId(null);
      setQuoteIntakeMessages([]);
      // The URL handle goes with it. Left behind, a reload would resurrect the
      // conversation this block just abandoned — the resume effect would read
      // the param and restore the transcript the consumer walked away from.
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete(INTAKE_CONVERSATION_PARAM);
          return next;
        },
        { replace: true },
      );
      setQuoteAnticipatedDocuments([]);
      setQuoteIntakeAnswers({});
      setQuoteDraftDisciplines([]);
      setQuoteIntakeOutOfScopeMessage(null);
        setQuoteIntakeTurnsRemaining(QUOTE_INTAKE_MAX_TURNS);
    }
  }, [
    goBackToTypeOrCategory,
    incomeInnerStep,
    incomeReviewPhase,
    isChooseEnterpriseProduct,
    isChooseType,
    isQuoteRequest,
    quoteRequestPhase,
    isFillEnterpriseProduct,
    isPreconditionCheck,
    isIncomeCertificateFlow,
    isLegalEntityFlow,
    legalEntityPhase,
    selectedEnterpriseProduct,
    setSearchParams,
  ]);

  const runIncomePrimaryAction = useCallback((): void => {
    void incomeWizardRef.current?.runPrimaryAction();
  }, []);

  // ---------------------------------------------------------------------------
  // Guest-checkout persistence: snapshot the summary-step state to IndexedDB before
  // Google SSO redirects away, hydrate on the wizard's next mount. Only summary-step
  // shapes are persisted (income_certificate summary, legal_entity summary, and the
  // simpler single-click product types) — partial mid-wizard state is intentionally
  // not preserved across the SSO redirect since it would require snapshotting the
  // income-form sub-hook's ~25 state slots. Users who SSO mid-wizard re-enter the
  // earlier steps; users who SSO from summary keep everything.
  // ---------------------------------------------------------------------------

  const snapshotWizardForSsoRedirect = useCallback((): boolean => {
    const uuid = getOrCreateWizardSessionUuid();

    if (step === "incomeCertificate" && incomeReviewPhase === "summary" && incomeReviewPayload) {
      void snapshotWizardState(uuid, {
        kind: "income_summary",
        selectedIntentSlug,
        category,
        requestType,
        incomeReviewPayload,
        incomeInvoiceIncludeRut,
        incomeInvoiceRutDigits,
      });
      return true;
    }

    if (
      step === "legalEntityDetails" &&
      legalEntityPhase === "summary" &&
      validatedCompany != null &&
      estatutoFile != null &&
      comunicacionBcuFile != null
    ) {
      void snapshotWizardState(uuid, {
        kind: "legal_entity_summary",
        selectedIntentSlug,
        category,
        requestType,
        rut,
        validatedCompany,
        legalEntityManualMode,
        legalEntityManualFields,
        estatutoFile,
        comunicacionBcuFile,
        legalDocumentType,
        legalDocumentFrontFile,
        legalDocumentBackFile,
        passportDocumentFile,
        legalEntityFulfillmentMode,
        legalEntityInvoiceIncludeRut,
        legalEntityInvoiceRutDigits,
        legalEntityCompanyRutMode,
      });
      return true;
    }

    /*
      THE DRAFT-REVIEW STEP IS NOW A GATE, so it is now a snapshot point.

      The account is asked for here (spec b5b59843), which means SSO can leave
      from here — and SSO is a full page load, so anything not written down is
      gone. Before this branch existed the phase could not reach an auth modal
      at all.

      intakeConversationId is the field that matters. Without it the visitor
      returns, the request is created with no conversation attached, and the
      backend re-runs the one-shot classifier over a transcript it already
      analysed — a worse classification of work they already did, with nothing
      visibly broken to explain it.
    */
    if (
      step === "quoteRequest" &&
      quoteRequestPhase === "form" &&
      quoteRequestTitle.trim().length > 0 &&
      quoteRequestDescription.trim().length > 0
    ) {
      void snapshotWizardState(uuid, {
        kind: "quote_request_draft_review",
        selectedIntentSlug,
        quoteRequestTitle,
        quoteRequestDescription,
        quoteRequestAttachments,
        intakeConversationId: quoteIntakeDraftConversationId,
      });
      return true;
    }

    if (
      step === "quoteRequest" &&
      quoteRequestPhase === "summary" &&
      quoteRequestTitle.trim().length > 0 &&
      quoteRequestDescription.trim().length > 0
    ) {
      void snapshotWizardState(uuid, {
        kind: "quote_request_summary",
        selectedIntentSlug,
        quoteRequestTitle,
        quoteRequestDescription,
        quoteRequestAttachments,
        // Carried here too: a visitor can still reach the summary anonymously
        // if they arrived with a session that expired mid-wizard.
        intakeConversationId: quoteIntakeDraftConversationId,
      });
      return true;
    }

    if (step === "chooseType" && requestType != null) {
      // Simple notary / accountant types submit immediately on type selection. The
      // modal opens at the moment of click but BEFORE the submit fires, so we save
      // the requestType to re-fire on return.
      void snapshotWizardState(uuid, {
        kind: "pending_type_submit",
        selectedIntentSlug,
        category,
        requestType,
      });
      return true;
    }

    return false;
  }, [
    step,
    incomeReviewPhase,
    incomeReviewPayload,
    incomeInvoiceIncludeRut,
    incomeInvoiceRutDigits,
    selectedIntentSlug,
    category,
    requestType,
    legalEntityPhase,
    validatedCompany,
    estatutoFile,
    comunicacionBcuFile,
    rut,
    legalEntityManualMode,
    legalEntityManualFields,
    legalDocumentType,
    legalDocumentFrontFile,
    legalDocumentBackFile,
    passportDocumentFile,
    legalEntityFulfillmentMode,
    legalEntityInvoiceIncludeRut,
    legalEntityInvoiceRutDigits,
    legalEntityCompanyRutMode,
    quoteRequestPhase,
    quoteRequestTitle,
    quoteRequestDescription,
    quoteRequestAttachments,
  ]);

  /** Wipe IndexedDB + session UUID after the wizard completes successfully or is cancelled. */
  const clearPersistedWizardSnapshot = useCallback((): void => {
    const uuid = getOrCreateWizardSessionUuid();
    void clearWizardState(uuid);
    clearWizardSessionUuid();
  }, []);

  const hasHydratedRef = useRef(false);

  useEffect(() => {
    if (hasHydratedRef.current) return;
    hasHydratedRef.current = true;
    const uuid = getOrCreateWizardSessionUuid();
    void hydrateWizardState<{ kind: string; [k: string]: unknown }>(uuid).then((snap) => {
      if (!snap) return;
      if (snap.kind === "income_summary") {
        setSelectedIntentSlug((snap.selectedIntentSlug as string | null) ?? null);
        setCategory((snap.category as RequestCategory | null) ?? null);
        setRequestType((snap.requestType as string | null) ?? null);
        setIncomeReviewPayload(snap.incomeReviewPayload as IncomeCertificateSubmitPayload);
        setIncomeInvoiceIncludeRut(Boolean(snap.incomeInvoiceIncludeRut));
        setIncomeInvoiceRutDigits((snap.incomeInvoiceRutDigits as string) ?? "");
        setIncomeReviewPhase("summary");
        setStep("incomeCertificate");
        return;
      }
      if (snap.kind === "legal_entity_summary") {
        setSelectedIntentSlug((snap.selectedIntentSlug as string | null) ?? null);
        setCategory((snap.category as RequestCategory | null) ?? null);
        setRequestType((snap.requestType as string | null) ?? null);
        setRut((snap.rut as string) ?? "");
        setValidatedCompany((snap.validatedCompany as ValidateRutResponse | null) ?? null);
        setLegalEntityManualMode(Boolean(snap.legalEntityManualMode));
        setLegalEntityManualFields(
          (snap.legalEntityManualFields as ManualCompanyFieldsState) ?? EMPTY_MANUAL_COMPANY_FIELDS,
        );
        setEstatutoFile((snap.estatutoFile as File | null) ?? null);
        setComunicacionBcuFile((snap.comunicacionBcuFile as File | null) ?? null);
        setLegalDocumentType((snap.legalDocumentType as LegalDocumentType) ?? "national_id");
        setLegalDocumentFrontFile((snap.legalDocumentFrontFile as File | null) ?? null);
        setLegalDocumentBackFile((snap.legalDocumentBackFile as File | null) ?? null);
        setPassportDocumentFile((snap.passportDocumentFile as File | null) ?? null);
        setLegalEntityFulfillmentMode((snap.legalEntityFulfillmentMode as string) ?? "physical_only");
        setLegalEntityInvoiceIncludeRut(Boolean(snap.legalEntityInvoiceIncludeRut));
        setLegalEntityInvoiceRutDigits((snap.legalEntityInvoiceRutDigits as string) ?? "");
        setLegalEntityCompanyRutMode(
          (snap.legalEntityCompanyRutMode as LegalEntityCompanyRutMode) ?? "other",
        );
        setLegalEntityPhase("summary");
        setStep("legalEntityDetails");
        return;
      }
      /*
        THE SNAPSHOT IS AN UNTYPED BAG, so a kind written with no matching
        branch here is simply dropped — written, never read, nothing to see.
        That is the failure this branch exists to avoid: the draft-review step
        became a snapshot point when it became the account gate, and without a
        restore the visitor returns from SSO to the start of the wizard.
      */
      if (snap.kind === "quote_request_draft_review") {
        setSelectedIntentSlug((snap.selectedIntentSlug as string | null) ?? null);
        setRequestType("quote_request");
        setQuoteRequestTitle((snap.quoteRequestTitle as string) ?? "");
        setQuoteRequestDescription((snap.quoteRequestDescription as string) ?? "");
        setQuoteRequestAttachments(
          (snap.quoteRequestAttachments as QuoteRequestAttachmentUpload[]) ?? [],
        );
        // THE FIELD THE WHOLE BRANCH IS FOR. Without it the request is created
        // with no conversation attached and the backend re-runs the one-shot
        // classifier over a transcript it already analysed.
        setQuoteIntakeDraftConversationId(
          (snap.intakeConversationId as string | null) ?? null,
        );
        setQuoteRequestPhase("form");
        setStep("quoteRequest");
        return;
      }
      if (snap.kind === "quote_request_summary") {
        setSelectedIntentSlug((snap.selectedIntentSlug as string | null) ?? null);
        setRequestType("quote_request");
        setQuoteRequestTitle((snap.quoteRequestTitle as string) ?? "");
        setQuoteRequestDescription((snap.quoteRequestDescription as string) ?? "");
        // Files never survive the SSO redirect (a File is not serialisable),
        // so this restores an empty list either way — the cast just keeps the
        // snapshot's shape honest about what it holds.
        setQuoteRequestAttachments(
          (snap.quoteRequestAttachments as QuoteRequestAttachmentUpload[]) ?? [],
        );
        setQuoteIntakeDraftConversationId(
          (snap.intakeConversationId as string | null) ?? null,
        );
        setQuoteRequestPhase("summary");
        setStep("quoteRequest");
        return;
      }
      if (snap.kind === "pending_type_submit") {
        setSelectedIntentSlug((snap.selectedIntentSlug as string | null) ?? null);
        setCategory((snap.category as RequestCategory | null) ?? null);
        setRequestType((snap.requestType as string | null) ?? null);
        setStep("chooseType");
        return;
      }
    });
     
  }, []);

  return {
    step,
    category,
    requestType,
    setRequestType,
    /**
     * Payment method for every checkout this hook creates. Surfaces should
     * render a selector and call setPaymentMethod; anything that does not keeps
     * the card flow it has today, because the default is "dlocal".
     */
    paymentMethod,
    setPaymentMethod,
    allowedTypes,
    rut,
    setRut,
    validatedCompany,
    legalEntityEffectiveCompany,
    legalEntityManualMode,
    legalEntityManualFields,
    updateLegalEntityManualField,
    isValidatingRut,
    estatutoFile,
    setEstatutoFile,
    comunicacionBcuFile,
    setComunicacionBcuFile,
    legalDocumentType,
    setLegalDocumentType,
    legalDocumentFrontFile,
    setLegalDocumentFrontFile,
    legalDocumentBackFile,
    setLegalDocumentBackFile,
    passportDocumentFile,
    setPassportDocumentFile,
    incomeInnerStep,
    setIncomeInnerStep,
    incomeForm,
    incomePrimaryEnabled,
    setIncomePrimaryEnabled,
    incomeWizardRef,
    incomeFlowPhase,
    catalogQuery,
    certificateRequestIntentsQuery,
    selectedIntentSlug,
    handleIntentChange,
    handleCertificateTypeChange,
    handleRutInputChange,
    handleValidateRut,
    handleContinueFromIntent,
    handleContinueFromType,
    handleContinueLegalEntityToSummary,
    handleLegalEntityFooterPrimary,
    legalEntityPhase,
    handleContinueQuoteRequestToSummary,
    quoteIntakeMessages,
    quoteIntakeThinking,
    quoteIntakeTurnsRemaining,
    quoteIntakeOutOfScopeMessage,
    /*
      Exposed so the page can tell the composer whether the NEXT message will
      mint a guest identity, which is the only message that needs a captcha
      (control C-6). `quoteIntakeMessages.length === 0` is not a substitute:
      the composer renders the consumer's own line optimistically, so after a
      refused start there is a message but still no conversation — exactly the
      moment the widget must stay up.
    */
    quoteIntakeConversationId,
    sendOutOfScopeAnyway,
    quoteDeliveryAddressSelection,
    quoteDeliveryOptedOut,
    setQuoteDeliveryOptedOut,
    setQuoteDeliveryAddressSelection,
    quoteDeliveryAddressId,
    setQuoteDeliveryAddressId,
    handleContinueQuoteRequestToSummaryFromDelivery,
    quoteAnticipatedDocuments,
    quoteIntakeAnswers,
    setQuoteIntakeAnswers,
    quoteIntakeDetails,
    quoteDraftDisciplines,
    /**
     * True when the form is showing what the conversation drafted, rather than
     * the empty fallback form. The two share a phase but ask for different
     * things: one is "review this", the other is "write this".
     */
    isQuoteDraftReview: quoteIntakeDraftConversationId != null,
    handleQuoteIntakeSend,
    fallbackToQuoteRequestForm,
    quoteRequestPhase,
    submitIncomeCertificate,
    incomeReviewPhase,
    incomeReviewPayload,
    incomeChargeTotalLabel,
    handleIncomeStep3ContinueToReview,
    completeIncomeCertificateFromSummary,
    runIncomePrimaryAction,
    handleHeaderBack,
    isIncomeCertificateFlow,
    isLegalEntityFlow,
    isQuoteRequest,
    isChooseIntent,
    isChooseType,
    isChooseEnterpriseProduct,
    isPreconditionCheck,
    isFillEnterpriseProduct,
    isWorkflowSteps,
    enterpriseCatalogQuery,
    selectedEnterpriseProduct,
    setSelectedEnterpriseProduct,
    handleSelectEnterpriseProduct,
    handleCompleteWorkflowStep,
    activeEnterpriseRequest,
    workflowViewNodeId,
    pendingStepData,
    enterpriseFormSubmitting,
    isWizardSubmitting,
    isCertificatePaymentBusy,
    isPaymentResumeRunning,
    paymentResumeProgress,
    legalEntityChargeTotalLabel,
    canContinueIntent,
    canContinueType,
    canSubmitLegalEntity,
    legalEntityFulfillmentMode,
    setLegalEntityFulfillmentMode,
    legalEntityInvoiceIncludeRut,
    setLegalEntityInvoiceIncludeRut,
    legalEntityInvoiceRutDigits,
    setLegalEntityInvoiceRutDigits,
    incomeInvoiceIncludeRut,
    setIncomeInvoiceIncludeRut,
    incomeInvoiceRutDigits,
    setIncomeInvoiceRutDigits,
    legalEntityInvoiceValid,
    incomeInvoiceValid,
    invoiceRutLockedToRegistration,
    effectiveLegalEntityInvoiceIncludeRut,
    effectiveLegalEntityInvoiceRutDigits,
    effectiveIncomeInvoiceIncludeRut,
    effectiveIncomeInvoiceRutDigits,
    legalEntityCompanyRutMode,
    registeredCompanySnapshot:
      user?.consumerAccountKind === "company" && registrationInvoiceRutDigits != null
        ? user.consumerCompanyRegistration ?? null
        : null,
    selectLegalEntitySessionCompany,
    selectLegalEntityOtherCompany,
    returnToLegalEntityCompanyChoice,
    snapshotWizardForSsoRedirect,
    clearPersistedWizardSnapshot,
    quoteRequestTitle,
    quoteRequestDescription,
    quoteRequestAttachments,
    setQuoteRequestTitle,
    setQuoteRequestDescription,
    setQuoteRequestAttachments,
    buildQuoteRequestPayload,
    createQuoteRequestMutation,
    handleQuoteRequestSubmit,
    quoteRequestBlockedKinds,
  };
}
